import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Typography,
  TextField,
  Button,
  IconButton,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { fi } from 'date-fns/locale';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { withTranslation } from 'react-i18next';

import { PICKER_MINUTES } from './calendarConstants';
import useCalendarState from './hooks/useCalendarState';
import { format, setTimeOfDay, timeOfDay, workScheduleHoursForDate } from './hooks/dateUtils';

// "Whole day" means the workshop's own opening hours, not midnight to midnight: a note
// drawn from 00:00 to 23:59 would tower over a calendar that only shows the work day. The hours
// are per weekday: the start date's opening time to the end date's closing time.
const isWholeDay = (start, end, workSchedule) =>
  !!start && !!end
  && timeOfDay(start) === workScheduleHoursForDate(workSchedule, start).startHour
  && timeOfDay(end) === workScheduleHoursForDate(workSchedule, end).endHour;

const toWholeDay = (start, end, workSchedule) => {
  const wholeStart = setTimeOfDay(new Date(start), workScheduleHoursForDate(workSchedule, start).startHour);
  const wholeEnd = new Date(end || start);
  setTimeOfDay(wholeEnd, workScheduleHoursForDate(workSchedule, wholeEnd).endHour);
  return { startDateTime: wholeStart, endDateTime: wholeEnd };
};

function CalendarNoteDialog({ t }) {
  const {
    noteDialogOpen,
    closeNoteDialog,
    selectedTimeSlot,
    editingNote,
    employees,
    workSchedule,
    addCalendarNote,
    updateCalendarNote,
    removeCalendarNote,
  } = useCalendarState();

  const isEditMode = !!editingNote;

  // The phone's calendar (CalendarPage's breakpoint): no keyboard, so no shortcut hint
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Default span without a clicked slot: today's work day (the week's bounds on a closed day)
  const { startHour: startHourInt, endHour: endHourInt } = workScheduleHoursForDate(workSchedule, new Date());

  const [formData, setFormData] = useState({
    content: '',
    employeeId: '',
    allDay: false,
    startDateTime: null,
    endDateTime: null,
  });

  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Initialize form when dialog opens
  useEffect(() => {
    if (noteDialogOpen) {
      let initialData;

      if (isEditMode && editingNote) {
        const editStart = new Date(editingNote.start_time);
        const editEnd = new Date(editingNote.end_time);
        initialData = {
          content: editingNote.content || '',
          employeeId: editingNote.employee_id || '',
          allDay: isWholeDay(editStart, editEnd, workSchedule),
          startDateTime: editStart,
          endDateTime: editEnd,
        };
      } else {
        let startDateTime, endDateTime;

        if (selectedTimeSlot) {
          const startDate = selectedTimeSlot.startDate || selectedTimeSlot.date;
          const endDate = selectedTimeSlot.endDate || selectedTimeSlot.date;

          startDateTime = new Date(startDate);
          startDateTime.setHours(Math.floor(selectedTimeSlot.startHour), Math.round((selectedTimeSlot.startHour % 1) * 60), 0, 0);

          endDateTime = new Date(endDate);
          endDateTime.setHours(Math.floor(selectedTimeSlot.endHour), Math.round((selectedTimeSlot.endHour % 1) * 60), 0, 0);
        } else {
          startDateTime = new Date();
          setTimeOfDay(startDateTime, startHourInt);
          endDateTime = new Date(startDateTime);
          setTimeOfDay(endDateTime, endHourInt);
        }

        initialData = {
          content: '',
          // Selecting inside a mechanic's column pins the note to that column; the
          // combined view has no column, so the note belongs to the whole workshop.
          employeeId:
            selectedTimeSlot?.resourceType === 'employee' ? selectedTimeSlot.resourceId : '',
          allDay: isWholeDay(startDateTime, endDateTime, workSchedule),
          startDateTime,
          endDateTime,
        };
      }

      setFormData(initialData);
      setDeleteConfirm(false);
      setSaveError(null);
    }
  }, [noteDialogOpen, selectedTimeSlot, isEditMode, editingNote, startHourInt, endHourInt, workSchedule]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      // "Whole day" is a shortcut past the pickers: it stretches the note from the
      // workshop's opening time to its closing time and leaves the dates alone.
      if (field === 'allDay' && value && prev.startDateTime) {
        return { ...newData, ...toWholeDay(prev.startDateTime, prev.endDateTime, workSchedule) };
      }

      if (field === 'startDateTime' && value && newData.endDateTime) {
        const minEnd = new Date(value.getTime() + PICKER_MINUTES * 60 * 1000);
        if (newData.endDateTime <= value) {
          newData.endDateTime = minEnd;
        }
      }
      if (field === 'endDateTime' && value && newData.startDateTime) {
        const minEnd = new Date(newData.startDateTime.getTime() + PICKER_MINUTES * 60 * 1000);
        if (value < minEnd) {
          newData.endDateTime = minEnd;
        }
      }

      return newData;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSaveError(null);

    try {
      const startDateTime = formData.startDateTime;
      const endDateTime = formData.endDateTime;

      const payload = {
        content: formData.content,
        employee: formData.employeeId || null,
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
      };

      if (isEditMode) {
        const response = await axios.patch(
          `/api/scheduling/calendar-notes/${editingNote.id}/`,
          payload
        );

        updateCalendarNote(editingNote.id, {
          ...response.data,
          employee_id: response.data.employee || null,
          local_start_date: format(startDateTime, 'yyyy-MM-dd'),
          local_end_date: format(endDateTime, 'yyyy-MM-dd'),
          local_start_hour: startDateTime.getHours() + startDateTime.getMinutes() / 60,
          local_end_hour: endDateTime.getHours() + endDateTime.getMinutes() / 60,
        });
      } else {
        const response = await axios.post('/api/scheduling/calendar-notes/', payload);

        addCalendarNote({
          ...response.data,
          employee_id: response.data.employee || null,
          local_start_date: format(startDateTime, 'yyyy-MM-dd'),
          local_end_date: format(endDateTime, 'yyyy-MM-dd'),
          local_start_hour: startDateTime.getHours() + startDateTime.getMinutes() / 60,
          local_end_hour: endDateTime.getHours() + endDateTime.getMinutes() / 60,
        });
      }

      closeNoteDialog();
    } catch (error) {
      console.error('Error saving calendar note:', error);
      const message = error.response?.data?.detail || error.response?.data?.message || t('Failed to save note');
      setSaveError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    setLoading(true);
    setSaveError(null);
    try {
      await axios.delete(`/api/scheduling/calendar-notes/${editingNote.id}/`);
      removeCalendarNote(editingNote.id);
      closeNoteDialog();
    } catch (error) {
      console.error('Error deleting calendar note:', error);
      const message = error.response?.data?.detail || error.response?.data?.message || t('Failed to delete note');
      setSaveError(message);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.content.trim() && formData.startDateTime && formData.endDateTime;

  return (
    <Dialog
      open={noteDialogOpen}
      onClose={closeNoteDialog}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {isEditMode ? t('Edit note') : t('Add note')}
        </Typography>
        <IconButton onClick={closeNoteDialog} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {saveError && (
          <Alert
            severity="error"
            onClose={() => setSaveError(null)}
            sx={{ mb: 2.5, borderRadius: '8px' }}
          >
            {saveError}
          </Alert>
        )}

        {/* Note Content */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Note content')}</Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            size="small"
            value={formData.content}
            onChange={(e) => handleFieldChange('content', e.target.value)}
            onKeyDown={(e) => {
              // Type and go: the whole point of a note is that it takes seconds to write.
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && isFormValid && !loading) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={t('Add note to calendar')}
            helperText={isMobile ? undefined : t('Ctrl + Enter saves')}
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </Box>

        {/* Which column the note sits in */}
        {employees.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Shown in column')}</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={formData.employeeId}
              onChange={(e) => handleFieldChange('employeeId', e.target.value)}
              data-testid="note-employee-select"
              // The whole-workshop choice is the empty value; without this the field reads
              // as blank, as if no column had been picked
              SelectProps={{ displayEmpty: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
            >
              <MenuItem value="">{t('Whole workshop')}</MenuItem>
              {employees.map((employee) => (
                <MenuItem key={employee.id} value={employee.id}>
                  {employee.name}
                </MenuItem>
              ))}
              {/* An employee hidden from the calendar still owns their old notes; without a
                  row for them the field would read as empty and look like a whole-workshop note. */}
              {formData.employeeId && !employees.some((e) => e.id === formData.employeeId) && (
                <MenuItem value={formData.employeeId}>
                  {editingNote?.employee_name || t('Employee')}
                </MenuItem>
              )}
            </TextField>
          </Box>
        )}

        {/* Whole day: the shortcut past both pickers */}
        <Box sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.allDay}
                onChange={(e) => handleFieldChange('allDay', e.target.checked)}
                data-testid="note-all-day"
              />
            }
            label={<Typography sx={{ fontSize: '14px' }}>{t('Whole day')}</Typography>}
          />
        </Box>

        {/* Start DateTime */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('Start time')}</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DateTimePicker
              value={formData.startDateTime}
              onChange={(value) => handleFieldChange('startDateTime', value)}
              ampm={false}
              disabled={formData.allDay}
              minutesStep={PICKER_MINUTES}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* End DateTime */}
        <Box sx={{ mb: 2.5 }}>
          <Typography sx={{ fontSize: '13px', fontWeight: 600, mb: 1 }}>{t('End time')}</Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
            <DateTimePicker
              value={formData.endDateTime}
              onChange={(value) => handleFieldChange('endDateTime', value)}
              ampm={false}
              disabled={formData.allDay}
              minutesStep={PICKER_MINUTES}
              minDateTime={formData.startDateTime || undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: 'small',
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between',
          // A phone's width: the buttons drop to a second row rather than break their labels
          flexWrap: 'wrap',
          rowGap: 1,
        }}
      >
        {isEditMode ? (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={loading}
            sx={{ borderRadius: '10px', textTransform: 'none' }}
          >
            {deleteConfirm ? t('Confirm delete') : t('Delete')}
          </Button>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={closeNoteDialog}
            disabled={loading}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              color: 'text.secondary',
              borderColor: 'divider',
            }}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !isFormValid}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              whiteSpace: 'nowrap',
              bgcolor: '#0ea5e9',
              '&:hover': { bgcolor: '#0284c7' },
            }}
          >
            {loading ? t('Saving...') : isEditMode ? t('Save') : t('Add note')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(CalendarNoteDialog);
